import { useTranslation } from 'react-i18next';
import {
  C1ChartCard,
  C1Chip,
  C1Donut,
  C1Panel,
  C1StatusList,
  C1Table,
  C1Tile,
  type C1Column,
  type C1StatusRow,
} from '@/components/c1';
import { CategoryBars, SERIES, SplitBar, TrendLine } from '../charts';
import type { InstitutionVocabParams } from '@/hooks/useInstitutionTerms';
import { EduCorePaths, type EduCorePath } from '@/lib/educoreLinks';
import type {
  AcademicOverviewInsights,
  OverviewAdmissionsFunnel,
  OverviewBoarding,
  OverviewFinance,
  OverviewPathwayRow,
  OverviewStaffing,
} from '../types';
import { toTrendPoints } from './shared';

type Money = (value: number) => { display: string; exact: string };
type NumberFormat = (value: number) => string;
type Open = (path: EduCorePath) => string | undefined;

const DASH = '—';

/**
 * The honest stand-in for a region whose backing service is down: the title stays
 * so the reader knows the region exists, and the sentence says why there are no
 * figures. Never zeroes — an outage drawn as an empty school is a lie with axes.
 */
export function UnavailableSection({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <C1Panel title={title}>
      <p className="insight-scopeNote">{t('insights.overview.exec.sectionUnavailable')}</p>
    </C1Panel>
  );
}

/* ── E. Academic pathways — one comparable row per unit. ─────────────────── */
export function PathwaysSection({
  data,
  vocab,
  locale,
  boardingCapability,
}: {
  data: AcademicOverviewInsights;
  vocab: InstitutionVocabParams;
  locale: string;
  boardingCapability: boolean;
}) {
  const { t } = useTranslation();
  const rows = data.population?.byPathway;
  if (!rows || rows.length === 0) return null;

  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const showBoarding =
    boardingCapability && rows.some((row) => typeof row.boardingCount === 'number');

  const percent = (value: number | null | undefined) =>
    typeof value === 'number' ? `${value}%` : DASH;

  const columns: Array<C1Column<OverviewPathwayRow>> = [
    {
      key: 'unit',
      header: t('insights.overview.exec.colUnit', vocab),
      cell: (row) => row.unitName,
      rowHeader: true,
    },
    {
      key: 'students',
      header: t('insights.overview.exec.colStudents', vocab),
      cell: (row) => number(row.students),
      align: 'end',
    },
    ...(showBoarding
      ? [
          {
            key: 'boarding',
            header: t('insights.overview.exec.colBoarding'),
            cell: (row: OverviewPathwayRow) =>
              typeof row.boardingCount === 'number' ? number(row.boardingCount) : DASH,
            align: 'end' as const,
          },
        ]
      : []),
    {
      key: 'attendance',
      header: t('insights.overview.exec.colAttendance'),
      // Null is "no register recorded", which is not 0% attendance.
      cell: (row) => percent(row.attendanceRate30d),
      align: 'end',
    },
    {
      key: 'average',
      header: t('insights.overview.exec.colAverage'),
      cell: (row) => percent(row.academicAverage),
      align: 'end',
    },
    {
      key: 'atRisk',
      header: t('insights.overview.exec.colAtRisk'),
      // A chip with the word beside the count once it is non-zero — the column
      // header alone should not be the only carrier of "this is the bad number".
      cell: (row) =>
        typeof row.atRisk === 'number' ? (
          row.atRisk > 0 ? (
            <C1Chip tone="warning">{number(row.atRisk)}</C1Chip>
          ) : (
            number(row.atRisk)
          )
        ) : (
          DASH
        ),
      align: 'end',
    },
  ];

  return (
    <C1Panel title={t('insights.overview.exec.pathwaysTitle', vocab)} flush>
      <C1Table
        caption={t('insights.overview.exec.pathwaysCaption', vocab)}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.unitCode}
      />
    </C1Panel>
  );
}

/* ── F. Attendance & academics — trend, the three rates, and distributions. ── */
export function AcademicsSection({
  data,
  vocab,
  locale,
}: {
  data: AcademicOverviewInsights;
  vocab: InstitutionVocabParams;
  locale: string;
}) {
  const { t } = useTranslation();
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const attendance = data.attendance;
  const academics = data.academics;
  const today = attendance.today;

  const rateRows: C1StatusRow[] = [];
  if (today) {
    rateRows.push({
      id: 'today',
      label: t('insights.overview.exec.rateToday'),
      value:
        today.state === 'NO_ACTIVITY' || today.rate === null
          ? t('insights.overview.exec.noRegisterRecorded')
          : `${today.rate}%`,
    });
  }
  if (attendance.last7Rate !== undefined) {
    rateRows.push({
      id: 'last7',
      label: t('insights.overview.exec.rate7'),
      value: attendance.last7Rate === null ? DASH : `${attendance.last7Rate}%`,
    });
  }
  if (attendance.rate !== null) {
    rateRows.push({
      id: 'last30',
      label: t('insights.overview.exec.rate30'),
      value: `${attendance.rate}%`,
    });
  }
  const belowThreshold = attendance.belowThreshold ?? academics?.atRisk;
  if (typeof belowThreshold === 'number' && belowThreshold > 0) {
    rateRows.push({
      id: 'below',
      label: t('insights.overview.exec.belowThresholdCount', {
        threshold: attendance.threshold,
        ...vocab,
      }),
      value: number(belowThreshold),
      tone: 'warning',
    });
  }

  const distributions = academics?.distributions?.filter((d) => d.bands.length > 0) ?? [];
  const multiScheme = distributions.length > 1;

  return (
    <div className="insight-charts">
      <TrendLine
        title={t('insights.overview.attendanceTrend', vocab)}
        points={toTrendPoints(attendance.series, locale)}
        threshold={attendance.threshold}
        thresholdLabel={t('insights.thresholdShort', { threshold: attendance.threshold })}
      />

      {rateRows.length > 0 && (
        <C1ChartCard title={t('insights.overview.exec.ratesTitle')}>
          <C1StatusList items={rateRows} />
          {academics && academics.passRate !== null && (
            <p className="insight-scopeNote">
              {t('insights.overview.exec.passRateLine', { rate: academics.passRate })}
            </p>
          )}
        </C1ChartCard>
      )}

      {/* Grade distributions, respecting schemes.
          ONE SCHEME → the donut (bands are shares of one total, the hole carries it).
          SEVERAL SCHEMES → one bar chart per scheme plus the normalised histogram.
          NEVER one ring mixing schemes: an A under one scheme and a 14/20 under
          another are not parts of a common whole, and a blended donut would draw
          exactly that claim. */}
      {multiScheme ? (
        <>
          {distributions.map((scheme) => (
            <CategoryBars
              key={scheme.schemeLabel}
              title={t('insights.overview.exec.schemeTitle', { scheme: scheme.schemeLabel })}
              data={scheme.bands.map((band) => ({
                key: band.grade,
                label: band.grade,
                value: band.count,
              }))}
            />
          ))}
          {academics?.normalized && academics.normalized.length > 0 && (
            <CategoryBars
              title={t('insights.overview.exec.normalizedTitle')}
              data={academics.normalized.map((bucket) => ({
                key: bucket.bucket,
                label: bucket.bucket,
                value: bucket.count,
              }))}
            />
          )}
        </>
      ) : distributions.length === 1 ? (
        <C1Donut
          title={t('insights.overview.exec.schemeTitle', {
            scheme: distributions[0].schemeLabel,
          })}
          slices={[...distributions[0].bands]
            .sort((a, b) => a.grade.localeCompare(b.grade))
            .map((band) => ({ key: band.grade, label: band.grade, value: band.count }))}
          format={number}
          centre={
            academics
              ? { value: number(academics.assessed), label: t('insights.overview.assessed') }
              : undefined
          }
        />
      ) : data.grades.distribution.length > 0 ? (
        <C1Donut
          title={t('insights.overview.gradeBands')}
          slices={[...data.grades.distribution]
            .sort((a, b) => a.letter.localeCompare(b.letter))
            .map((entry) => ({ key: entry.letter, label: entry.letter, value: entry.count }))}
          format={number}
          centre={{ value: number(data.grades.assessed), label: t('insights.overview.assessed') }}
        />
      ) : null}
    </div>
  );
}

/* ── G. Financial position — a main-content section with one progress bar. ── */
export function FinanceSection({
  finance,
  money,
  number,
  open,
}: {
  finance: OverviewFinance;
  money: Money;
  number: NumberFormat;
  open: Open;
}) {
  const { t } = useTranslation();
  const year = finance.year;
  // Same rule as the collection tile: an unreadable finance block costs this
  // section, not the page around it.
  if (!year) return null;

  const rows: C1StatusRow[] = [
    {
      id: 'billed',
      label: t('insights.overview.billed'),
      value: <span title={money(year.billed).exact}>{money(year.billed).display}</span>,
      href: open(EduCorePaths.fees),
    },
    {
      id: 'collected',
      label: t('insights.overview.collected'),
      value: <span title={money(year.collected).exact}>{money(year.collected).display}</span>,
      href: open(EduCorePaths.fees),
    },
    {
      id: 'outstanding',
      label: t('insights.overview.outstanding'),
      value: <span title={money(year.outstanding).exact}>{money(year.outstanding).display}</span>,
      tone: year.outstanding > 0 ? 'warning' : undefined,
      href: open(EduCorePaths.fees),
    },
  ];

  const statusMix = (finance.statusMix ?? []).filter((entry) => entry.count > 0);

  return (
    <C1Panel
      title={t('insights.overview.exec.financeTitle')}
      action={
        open(EduCorePaths.reports) ? (
          <a
            href={open(EduCorePaths.reports)}
            target="_blank"
            rel="noopener noreferrer"
            className="c1-tableToggle"
          >
            {t('insights.openInEducore')}
          </a>
        ) : undefined
      }
    >
      <p className="insight-scopeNote">{t('insights.overview.exec.financeScope')}</p>
      <C1StatusList items={rows} />

      {/* One bar: collected as a share of billed. Nothing billed → a sentence,
          because an empty track would read as a collection failure. */}
      {year.collectionRate === null ? (
        <p className="insight-scopeNote">{t('insights.overview.nothingBilled')}</p>
      ) : (
        <>
          <div
            className="insight-meter insight-meter--progress"
            role="meter"
            aria-valuenow={year.collectionRate}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${t('insights.overview.collectionRate')}: ${year.collectionRate}%`}
          >
            <span
              className="insight-meterFill"
              style={{ width: `${Math.min(year.collectionRate, 100)}%` }}
            />
          </div>
          <p className="insight-meterCaption">
            {t('insights.overview.collectionRate')}: {year.collectionRate}%
          </p>
        </>
      )}

      <p className="insight-scopeNote">
        {t('insights.overview.exec.thisMonthLine', {
          amount: money(finance.thisMonth.collected).display,
        })}
        {' · '}
        {t('insights.overview.paymentsCount', { count: finance.thisMonth.payments })}
      </p>

      {statusMix.length > 0 && (
        <SplitBar
          title={t('insights.overview.exec.statusMixTitle')}
          parts={statusMix.map((entry, index) => ({
            key: entry.status,
            label: t(`insights.overview.exec.billStatus.${entry.status}`, {
              defaultValue: entry.status,
            }),
            value: entry.count,
            color: SERIES[index % SERIES.length],
          }))}
          format={number}
        />
      )}
    </C1Panel>
  );
}

/* ── H. Admissions funnel — stages in order, stalls called out. ─────────── */
export function AdmissionsFunnelSection({
  funnel,
  number,
  open,
}: {
  funnel: OverviewAdmissionsFunnel;
  number: NumberFormat;
  open: Open;
}) {
  const { t } = useTranslation();
  const stages = [
    ['applications', funnel.applications],
    ['underReview', funnel.underReview],
    ['assessed', funnel.assessed],
    ['offered', funnel.offered],
    ['accepted', funnel.accepted],
    ['enrolled', funnel.enrolled],
  ] as const;

  return (
    <C1Panel
      title={
        funnel.cycleLabel
          ? `${t('insights.overview.exec.admissionsTitle')} · ${funnel.cycleLabel}`
          : t('insights.overview.exec.admissionsTitle')
      }
      action={
        open(EduCorePaths.admissions) ? (
          <a
            href={open(EduCorePaths.admissions)}
            target="_blank"
            rel="noopener noreferrer"
            className="c1-tableToggle"
          >
            {t('insights.openInEducore')}
          </a>
        ) : undefined
      }
    >
      {(funnel.pendingOver7Days ?? 0) > 0 || (funnel.placementScheduled ?? 0) > 0 ? (
        <p className="insight-chipRow">
          {(funnel.pendingOver7Days ?? 0) > 0 && (
            <C1Chip tone="warning">
              {t('insights.overview.exec.pendingOver7', { count: funnel.pendingOver7Days })}
            </C1Chip>
          )}
          {(funnel.placementScheduled ?? 0) > 0 && (
            <C1Chip>
              {t('insights.overview.exec.placementScheduled', {
                count: funnel.placementScheduled,
              })}
            </C1Chip>
          )}
        </p>
      ) : null}
      <CategoryBars
        title={t('insights.overview.exec.funnelChartTitle')}
        data={stages.map(([key, value]) => ({
          key,
          label: t(`insights.overview.exec.stage.${key}`),
          value,
        }))}
      />
      <span className="sr-only">
        {stages.map(([key, value]) => `${t(`insights.overview.exec.stage.${key}`)}: ${number(value)}`).join(', ')}
      </span>
    </C1Panel>
  );
}

/* ── I. Boarding — occupancy and the day's exceptions. ──────────────────── */
export function BoardingSection({
  boarding,
  number,
}: {
  boarding: OverviewBoarding;
  number: NumberFormat;
}) {
  const { t } = useTranslation();
  const residences = boarding.residences ?? [];

  return (
    <C1Panel title={t('insights.overview.exec.boardingTitle')}>
      <div className="insight-tiles insight-tiles--compact">
        <C1Tile label={t('insights.overview.exec.boarders')} value={number(boarding.boarders)} />
        <C1Tile label={t('insights.overview.exec.checkedIn')} value={number(boarding.checkedIn)} />
        <C1Tile label={t('insights.overview.exec.onLeave')} value={number(boarding.onLeave)} />
        <C1Tile
          label={t('insights.overview.exec.unresolved')}
          value={number(boarding.unresolved)}
          tone={boarding.unresolved > 0 ? 'warning' : 'default'}
        />
      </div>

      {boarding.occupancyRate === null ? (
        // Capacity nobody recorded is not an empty residence.
        <p className="insight-scopeNote">{t('insights.overview.exec.occupancyUnknown')}</p>
      ) : (
        <>
          <div
            className="insight-meter insight-meter--progress"
            role="meter"
            aria-valuenow={boarding.occupancyRate}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${t('insights.overview.exec.occupancy')}: ${boarding.occupancyRate}%`}
          >
            <span
              className="insight-meterFill"
              style={{ width: `${Math.min(boarding.occupancyRate, 100)}%` }}
            />
          </div>
          <p className="insight-meterCaption">
            {t('insights.overview.exec.occupancy')}: {boarding.occupancyRate}%
          </p>
        </>
      )}

      {residences.length > 0 && (
        <>
          <h4 className="insight-subheading">{t('insights.overview.exec.residences')}</h4>
          <ul className="insight-bars">
            {residences.map((residence) => (
              <li key={residence.name} className="insight-bar">
                <span className="insight-barLabel">{residence.name}</span>
                <span className="insight-barTrack">
                  <span
                    className="insight-barFill"
                    style={{
                      width: `${Math.min(residence.occupancyRate ?? 0, 100)}%`,
                      background: SERIES[1],
                    }}
                    aria-hidden="true"
                  />
                </span>
                <span className="insight-barValue">
                  {residence.occupancyRate === null ? DASH : `${residence.occupancyRate}%`}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </C1Panel>
  );
}

/* ── J. Staffing — the roster, and the groups nobody teaches. ───────────── */
export function StaffingSection({
  staffing,
  vocab,
  number,
}: {
  staffing: OverviewStaffing;
  vocab: InstitutionVocabParams;
  number: NumberFormat;
}) {
  const { t } = useTranslation();
  const rows: C1StatusRow[] = [
    {
      id: 'total',
      label: t('insights.overview.exec.totalStaff'),
      value: number(staffing.total),
    },
    {
      id: 'teaching',
      label: t('insights.overview.exec.teaching', vocab),
      value: number(staffing.teaching),
    },
    {
      id: 'administrative',
      label: t('insights.overview.exec.administrative'),
      value: number(staffing.administrative),
    },
  ];

  return (
    <C1Panel title={t('insights.overview.exec.staffTitle')}>
      <C1StatusList items={rows} />
      {staffing.unassignedClassGroups > 0 && (
        <p className="insight-chipRow">
          <C1Chip tone="warning">
            {t('insights.overview.exec.unassignedGroups', {
              count: staffing.unassignedClassGroups,
              ...vocab,
            })}
          </C1Chip>
        </p>
      )}
    </C1Panel>
  );
}
